/**
 * Phase 128 — Super Admin WhatsApp Templates Console
 * ---------------------------------------------------
 * Lists registered transactional templates, lets ops test-send any template
 * with custom variables to validate Meta template approvals, and exposes
 * the recent dispatch audit log.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import '@/styles/livex.css';
import {
  ArrowLeft, MessageCircle, Send, Loader2, AlertTriangle, Check, X,
  CheckCircle2, Clock, Search, Shield,
} from 'lucide-react';
import WhatsAppMetricsStrip from '@/components/admin/WhatsAppMetricsStrip';

function StatusPill({ status }) {
  const cfg = {
    sent:                    { bg: 'bg-emerald-600', text: 'text-white', label: 'sent' },
    mocked:                  { bg: 'bg-amber-600',   text: 'text-white', label: 'mocked' },
    failed:                  { bg: 'bg-rose-600',    text: 'text-white', label: 'failed' },
    skipped_no_recipient:    { bg: 'bg-slate-600',   text: 'text-white', label: 'skipped · no phone' },
  }[status] || { bg: 'bg-slate-700', text: 'text-white', label: status || 'unknown' };
  return <span className={`text-[10px] font-black uppercase tracking-[0.1em] px-2 py-1 rounded ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>;
}

function fmtTime(iso) {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso?.slice(0, 19) || ''; }
}

export default function AdminWhatsAppConsole() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [metricsDays, setMetricsDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterKey, setFilterKey] = useState('');
  const [testModal, setTestModal] = useState(null);

  const reload = async () => {
    setError('');
    try {
      const [t, d, m] = await Promise.all([
        api.get('/admin/whatsapp/templates'),
        api.get(`/admin/whatsapp/dispatches${filterKey ? `?template_key=${filterKey}` : ''}`),
        api.get(`/admin/whatsapp/metrics?days=${metricsDays}`),
      ]);
      setTemplates(t.data?.entries || []);
      setDispatches(d.data?.entries || []);
      setMetrics(m.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load');
    }
  };

  useEffect(() => {
    setLoading(true);
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, metricsDays]);

  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="admin-whatsapp-console">
      <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate('/admin/livex')}
            data-testid="admin-whatsapp-back"
            className="inline-flex items-center gap-2 text-sm font-bold text-white hover:text-[#FF5A1F] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Super Admin
          </button>
          <div className="text-xs tracking-[0.3em] font-black text-[#22c55e]">WHATSAPP · CONSOLE</div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
              <MessageCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-[11px] font-black tracking-[0.18em] uppercase text-[#22c55e]">Phase 128 · Twilio WhatsApp</div>
              <h1 className="text-2xl sm:text-3xl font-black text-white">Transactional Templates</h1>
            </div>
          </div>
          <p className="text-sm font-medium text-[#94A3B8]">
            Manage Meta-approved templates, preview rendered text, and run sandbox test sends to opted-in numbers.
            Once Meta approves a template, paste the ContentSid (HX…) into <code className="text-emerald-300">env_sid_key</code> in
            backend/.env and restart — the dispatcher auto-flips to ContentSid mode.
          </p>
        </div>

        {error && (
          <div data-testid="admin-whatsapp-error" className="p-3 bg-rose-950 border-2 border-rose-700 rounded-xl text-rose-100 text-sm font-bold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        )}

        {/* Phase 128-UX-Metrics — 7-day platform telemetry strip */}
        {!loading && metrics && (
          <WhatsAppMetricsStrip
            metrics={metrics}
            templates={templates}
            days={metricsDays}
            onDaysChange={(d) => setMetricsDays(d)}
          />
        )}

        {/* Templates */}
        {loading ? (
          <div className="p-8 text-center text-[#94A3B8] font-bold" data-testid="admin-whatsapp-loading">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {templates.map((t) => (
              <div key={t.key} className="p-5 bg-[#07111F] border-2 border-[#22314F] hover:border-[#22c55e] rounded-2xl transition-all"
                data-testid={`admin-whatsapp-template-${t.key}`}>
                <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.15em] text-[#22c55e]">{t.label}</div>
                    <h3 className="text-base font-black text-white mt-0.5 font-mono">{t.key}</h3>
                  </div>
                  <span className={`text-[10px] font-black uppercase tracking-[0.1em] px-2 py-1 rounded inline-flex items-center gap-1 ${t.sid_configured ? 'bg-emerald-700 text-white' : 'bg-amber-700 text-white'}`}>
                    {t.sid_configured ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                    {t.sid_configured ? 'SID configured' : 'Free-form mode'}
                  </span>
                </div>
                <p className="text-[12px] font-medium text-[#cbd5e1] mb-3">{t.purpose}</p>
                <div className="p-3 bg-[#050a14] border border-[#22314F] rounded-lg mb-3">
                  <div className="text-[10px] font-black uppercase tracking-[0.12em] text-[#94A3B8] mb-1">Body</div>
                  <pre className="text-[12px] text-white font-mono whitespace-pre-wrap leading-relaxed">{t.body_preview}</pre>
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span className="text-[10px] font-black uppercase tracking-[0.12em] text-[#94A3B8]">Variables:</span>
                  {t.vars.map((v) => (
                    <span key={v} className="text-[10px] font-bold font-mono px-2 py-0.5 bg-[#0c1a2e] border border-[#22314F] rounded text-emerald-300">{`{${v}}`}</span>
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setTestModal({ template: t, phone: '', variables: t.vars.reduce((acc, v) => ({ ...acc, [v]: '' }), {}), result: null })}
                    data-testid={`admin-whatsapp-test-${t.key}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white border-2 border-[#22c55e] rounded-lg transition-all hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)' }}>
                    <Send className="h-3 w-3" /> Test send
                  </button>
                  <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-[0.1em]">
                    env: <code className="text-emerald-300">{t.env_sid_key}</code>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Dispatch log */}
        <div className="p-5 bg-[#07111F] border-2 border-[#22314F] rounded-2xl">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-emerald-400" />
              <h2 className="text-base font-black text-white tracking-tight">Recent dispatches (audit)</h2>
              <span className="text-[10px] font-bold text-[#94A3B8] uppercase">{dispatches.length} entries</span>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94A3B8] pointer-events-none" />
              {/* Phase 141-B polish — entire <select> built via React.createElement
                  so the build-time source-map instrumentation doesn't wrap the
                  dynamic {templates.map(...)} expression in a <span style="display:
                  contents;">, which violates HTML's strict <select> child rule
                  and triggers a React hydration warning. */}
              {React.createElement(
                'select',
                {
                  value: filterKey,
                  onChange: (e) => setFilterKey(e.target.value),
                  'data-testid': 'admin-whatsapp-dispatch-filter',
                  className: 'pl-8 pr-3 py-2 bg-[#0c1a2e] border-2 border-[#22314F] rounded-lg text-xs font-bold text-white',
                },
                React.createElement('option', { key: '__all', value: '' }, 'All templates'),
                ...templates.map((t) =>
                  React.createElement('option', { key: t.key, value: t.key }, t.label)
                )
              )}
            </div>
          </div>
          {dispatches.length === 0 ? (
            <div className="text-center py-8 text-sm font-bold text-[#94A3B8]" data-testid="admin-whatsapp-dispatches-empty">
              <Send className="h-6 w-6 text-[#22314F] mx-auto mb-2" />
              No dispatches yet for this filter.
            </div>
          ) : (
            <div className="space-y-1.5">
              {dispatches.map((d) => (
                <div key={d.id} className="px-3 py-2 bg-[#0c1a2e] border border-[#22314F] rounded-lg flex flex-col gap-2" data-testid={`admin-whatsapp-dispatch-${d.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <StatusPill status={d.status} />
                      <div className="min-w-0">
                        <div className="text-[12px] font-bold text-white font-mono truncate">{d.template_key}</div>
                        <div className="text-[10px] font-bold text-[#94A3B8] mt-0.5">
                          → {d.target || '(no recipient)'} · {d.recipient_role} · {fmtTime(d.sent_at)}
                        </div>
                      </div>
                    </div>
                    {d.error && <span className="text-[10px] font-bold text-rose-300 truncate max-w-[260px]" title={d.error}>{d.error}</span>}
                  </div>
                  {d.rendered_body && (
                    <pre
                      className="text-[11px] font-mono text-[#cbd5e1] whitespace-pre-wrap leading-relaxed bg-[#050a14] p-2 rounded border border-[#22314F] line-clamp-3 max-h-24 overflow-hidden"
                      data-testid={`admin-whatsapp-dispatch-body-${d.id}`}
                    >{d.rendered_body}</pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Test Send modal */}
      {testModal && (
        <div
          data-testid="admin-whatsapp-test-modal"
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setTestModal(null); }}
        >
          <div className="w-full max-w-lg p-6 bg-[#07111F] border-2 border-[#22c55e] rounded-2xl text-white max-h-[85vh] overflow-y-auto"
            style={{ boxShadow: '0 24px 64px rgba(34,197,94,0.25)' }}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-[11px] font-black tracking-[0.18em] uppercase text-[#22c55e]">Test send · sandbox</div>
                <h2 className="text-xl font-black text-white mt-1">{testModal.template.label}</h2>
                <div className="text-[11px] font-bold text-[#94A3B8] mt-0.5 font-mono">{testModal.template.key}</div>
              </div>
              <button onClick={() => setTestModal(null)} className="text-[#94A3B8] hover:text-white" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <label className="block text-[11px] font-black uppercase tracking-[0.12em] text-[#94A3B8] mb-1">To phone (E.164, e.g. +919999900000)</label>
            <input
              value={testModal.phone}
              onChange={(e) => setTestModal({ ...testModal, phone: e.target.value, result: null })}
              data-testid="admin-whatsapp-test-modal-phone"
              placeholder="+919999900000"
              className="livex-input w-full mb-4"
            />

            <div className="space-y-2 mb-4">
              <div className="text-[11px] font-black uppercase tracking-[0.12em] text-[#94A3B8]">Variables</div>
              {testModal.template.vars.map((v) => (
                <div key={v}>
                  <label className="block text-[10px] font-bold text-emerald-300 font-mono mb-0.5">{`{${v}}`}</label>
                  <input
                    value={testModal.variables[v] || ''}
                    onChange={(e) => setTestModal({ ...testModal, variables: { ...testModal.variables, [v]: e.target.value }, result: null })}
                    data-testid={`admin-whatsapp-test-modal-var-${v}`}
                    placeholder={`Enter ${v}…`}
                    className="livex-input w-full"
                  />
                </div>
              ))}
            </div>

            {testModal.result && (
              <div className={`p-3 rounded-lg border-2 mb-3 ${testModal.result.success ? 'bg-emerald-950 border-emerald-700' : 'bg-amber-950 border-amber-700'}`}
                data-testid="admin-whatsapp-test-modal-result">
                <div className="text-[11px] font-black uppercase tracking-[0.12em] mb-2 flex items-center gap-1.5"
                  style={{ color: testModal.result.success ? '#6ee7b7' : '#fbbf24' }}>
                  {testModal.result.success ? <Check className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                  Test dispatch · {testModal.result.success ? 'sent' : (testModal.result.error || 'check audit log')}
                </div>
                <pre className="text-[11px] font-mono text-white whitespace-pre-wrap leading-relaxed bg-[#050a14] p-3 rounded border border-[#22314F]">{testModal.result.rendered_body}</pre>
              </div>
            )}

            <button
              onClick={async () => {
                try {
                  const { data } = await api.post('/admin/whatsapp/templates/test', {
                    template_key: testModal.template.key,
                    to_phone: testModal.phone,
                    variables: testModal.variables,
                  });
                  setTestModal({ ...testModal, result: { ...data.result, rendered_body: data.rendered_body } });
                  reload();
                } catch (e) {
                  setTestModal({ ...testModal, result: { success: false, error: e?.response?.data?.detail || 'Send failed', rendered_body: '' } });
                }
              }}
              data-testid="admin-whatsapp-test-modal-send"
              disabled={!testModal.phone.trim()}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold text-white rounded-lg border-2 border-[#22c55e] transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)' }}>
              <Send className="h-4 w-4" /> Send test message
            </button>
            <p className="text-[10px] text-[#94A3B8] mt-2 text-center font-bold">
              Sandbox sends only deliver to numbers that have opted in to Twilio's WhatsApp sandbox.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
